import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, CircleDot } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import { EmptyState, ErrorWithRetry, LoadingMessage } from "../components/UiStates";
import { MobileSectionsMenu } from "../components/MobileSectionsMenu";
import { ModeScopeBanner } from "../components/ModeScopeBanner";
import { ModeBadge } from "../components/ModeBadge";
import { PageLayout } from "../components/PageLayout";
import { WorkspaceNav } from "../components/WorkspaceNav";
import { useLanguage } from "../lib/LanguageContext";
import { formatPrice } from "../lib/formatTrade";
import { useTradingMode } from "../lib/TradingModeContext";
import { modeAccentBorderClass } from "../lib/modeStyles";
import { cn } from "../lib/cn";
import { WORKSPACE_ASIDE, WORKSPACE_SHELL_GRID, TABLE_SCROLL } from "../lib/workspaceLayout";
import { modeLabel } from "../lib/translations";
import { formatDateTime, formatTime } from "../lib/utils/locale";
import { Button } from "../components/ui/Button";
import { Card, CardTitle } from "../components/ui/Card";
import { LimitUsageBar } from "../components/LimitUsageBar";
import { TradingPnlSummary } from "../components/TradingPnlSummary";
import { BrokerPortfolioComparisonCard } from "../components/BrokerPortfolioComparisonCard";
import { apiClient } from "../services/api/client";
import { Link } from "react-router-dom";
const eventTicker = (event) => {
    if ("ticker" in event.payload && typeof event.payload.ticker === "string")
        return event.payload.ticker;
    return "";
};
const eventMatchesWatchlist = (event, watchlist) => {
    const ticker = eventTicker(event).toUpperCase();
    if (!ticker)
        return true;
    if (watchlist.length === 0)
        return true;
    return watchlist.includes(ticker);
};
const watchlistKey = (tickers) => [...tickers].sort().join("\0");
const eventPrice = (event) => {
    if ("price" in event.payload && typeof event.payload.price === "number")
        return event.payload.price;
    return null;
};
const eventSignal = (event) => {
    if ("signal" in event.payload && typeof event.payload.signal === "string")
        return event.payload.signal;
    return "";
};
const eventReasonCode = (event) => {
    if (!event)
        return "—";
    if ("reason_code" in event.payload && typeof event.payload.reason_code === "string" && event.payload.reason_code.length > 0) {
        return event.payload.reason_code;
    }
    return "—";
};
const eventPipelineStage = (event) => {
    if (!event)
        return "—";
    if ("pipeline_stage" in event.payload && typeof event.payload.pipeline_stage === "string" && event.payload.pipeline_stage.length > 0) {
        return event.payload.pipeline_stage;
    }
    return "—";
};
const AlertsCard = ({ t, lang, alerts, filteredAlerts, alertFilter, onFilterChange, onClearFiltered, onClearAll, onAck, }) => (_jsxs(Card, { className: "mt-6", children: [_jsxs("div", { className: "mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between", children: [_jsx(CardTitle, { children: t("monitor.alertsTitle") }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsxs("select", { value: alertFilter, onChange: (e) => onFilterChange(e.target.value), className: "h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground", children: [_jsx("option", { value: "all", children: t("monitor.alertFilterAll") }), _jsx("option", { value: "warning", children: t("monitor.alertFilterWarning") }), _jsx("option", { value: "error", children: t("monitor.alertFilterError") })] }), _jsx(Button, { type: "button", size: "sm", variant: "outlineMuted", onClick: onClearFiltered, disabled: filteredAlerts.length === 0, children: t("monitor.clearFiltered") }), _jsx(Button, { type: "button", size: "sm", variant: "outlineMuted", onClick: onClearAll, disabled: alerts.length === 0, children: t("monitor.clearAll") })] })] }), filteredAlerts.length === 0 ? (_jsx(EmptyState, { text: t("monitor.alertsEmpty") })) : (_jsx("ul", { className: "space-y-2", children: filteredAlerts.map((alert) => (_jsxs("li", { className: "flex items-start gap-2 rounded-lg border border-border p-3 text-sm", children: [alert.level === "error" ? (_jsx(AlertTriangle, { className: "mt-0.5 h-4 w-4 shrink-0 text-rose-500", "aria-hidden": true })) : (_jsx(CheckCircle2, { className: "mt-0.5 h-4 w-4 shrink-0 text-amber-500", "aria-hidden": true })), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-foreground", children: alert.message }), _jsx("p", { className: "text-xs text-muted-foreground", children: formatTime(alert.timestamp, lang) })] }), _jsx(Button, { type: "button", variant: "ghost", size: "sm", className: "ml-auto h-7 px-2 text-xs", onClick: () => onAck(alert.id), children: t("monitor.ack") })] }, alert.id))) }))] }));
const Dashboard = () => {
    const { t, lang } = useLanguage();
    const { session } = useAuth();
    const { mode } = useTradingMode();
    const [summary, setSummary] = useState(null);
    const [cockpit, setCockpit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [events, setEvents] = useState([]);
    const [showEventsHistory, setShowEventsHistory] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [isEventDetailsOpen, setIsEventDetailsOpen] = useState(false);
    const [chartScale, setChartScale] = useState("normalized");
    const [chartWindow, setChartWindow] = useState("1m");
    const [selectedTicker, setSelectedTicker] = useState("all");
    const [eventTypeFilter, setEventTypeFilter] = useState("all");
    const [eventSearch, setEventSearch] = useState("");
    const [alerts, setAlerts] = useState([]);
    const [alertFilter, setAlertFilter] = useState("all");
    const [lastWsEventAt, setLastWsEventAt] = useState(null);
    const [wsErrorAt, setWsErrorAt] = useState(null);
    const [lastMarketPrimaryAt, setLastMarketPrimaryAt] = useState(null);
    const [lastMarketFallbackAt, setLastMarketFallbackAt] = useState(null);
    const [lastMarketSource, setLastMarketSource] = useState(null);
    const [healthSnapshot, setHealthSnapshot] = useState(null);
    const [healthFetchError, setHealthFetchError] = useState(false);
    const [marketWatchlist, setMarketWatchlist] = useState([]);
    const eventDetailsRef = useRef(null);
    const lastFocusedElementRef = useRef(null);
    const seenEventIdsRef = useRef(new Set());
    const marketWatchlistRef = useRef(marketWatchlist);
    const cockpitRefreshTimerRef = useRef(null);
    const tRef = useRef(t);
    const langRef = useRef(lang);
    marketWatchlistRef.current = marketWatchlist;
    tRef.current = t;
    langRef.current = lang;
    const marketWatchlistKey = watchlistKey(marketWatchlist);
    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        const [dash, pit] = await Promise.all([apiClient.dashboard.get(mode), apiClient.dashboard.riskCockpit(mode)]);
        if (!dash.success) {
            setError(dash.error.message);
            setSummary(null);
            setCockpit(null);
            setLoading(false);
            return;
        }
        setSummary(dash.data);
        if (pit.success) {
            setCockpit(pit.data);
        }
        else {
            setCockpit(null);
        }
        setLoading(false);
    }, [mode]);
    useEffect(() => {
        void load();
    }, [load]);
    const refreshCockpit = useCallback(async () => {
        const pit = await apiClient.dashboard.riskCockpit(mode);
        if (pit.success) {
            setCockpit(pit.data);
        }
    }, [mode]);
    useEffect(() => {
        return () => {
            if (cockpitRefreshTimerRef.current !== null) {
                window.clearTimeout(cockpitRefreshTimerRef.current);
            }
        };
    }, []);
    useEffect(() => {
        let cancelled = false;
        const pollHealth = async () => {
            const result = await apiClient.health.get();
            if (cancelled)
                return;
            if (!result.success) {
                setHealthFetchError(true);
                setHealthSnapshot(null);
                return;
            }
            setHealthFetchError(false);
            setHealthSnapshot(result.data);
        };
        void pollHealth();
        const timer = window.setInterval(pollHealth, 30000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, []);
    useEffect(() => {
        let cancelled = false;
        const loadWatchlist = async () => {
            const result = await apiClient.market.watchlist(mode);
            if (!cancelled && result.success) {
                const next = result.data.tickers.map((ticker) => ticker.toUpperCase());
                setMarketWatchlist((prev) => (watchlistKey(prev) === watchlistKey(next) ? prev : next));
            }
        };
        void loadWatchlist();
        const timer = window.setInterval(() => void loadWatchlist(), 60000);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
        };
    }, [mode]);
    useEffect(() => {
        if (marketWatchlist.length === 0)
            return;
        setEvents([]);
        setAlerts([]);
        setLastWsEventAt(null);
        setWsErrorAt(null);
        setSelectedEventId(null);
        setSelectedTicker("all");
        setEventTypeFilter("all");
        setEventSearch("");
        setAlertFilter("all");
        setIsEventDetailsOpen(false);
        seenEventIdsRef.current = new Set();
        setLastMarketPrimaryAt(null);
        setLastMarketFallbackAt(null);
        setLastMarketSource(null);
        apiClient.realtime.subscribe(mode, ["market", "signal"], { watchlist_tickers: marketWatchlist });
        const unsubscribeEvent = apiClient.realtime.onEvent((event) => {
            const watchlist = marketWatchlistRef.current;
            if (event.mode !== mode)
                return;
            if (!eventMatchesWatchlist(event, watchlist))
                return;
            if (seenEventIdsRef.current.has(event.event_id))
                return;
            seenEventIdsRef.current.add(event.event_id);
            setLastWsEventAt(Date.now());
            if (event.event_type === "market.update") {
                const source = "source" in event.payload && (event.payload.source === "fallback" || event.payload.source === "primary")
                    ? event.payload.source
                    : "primary";
                setLastMarketSource(source);
                if (source === "primary")
                    setLastMarketPrimaryAt(Date.now());
                else
                    setLastMarketFallbackAt(Date.now());
                if (cockpitRefreshTimerRef.current !== null) {
                    window.clearTimeout(cockpitRefreshTimerRef.current);
                }
                cockpitRefreshTimerRef.current = window.setTimeout(() => {
                    cockpitRefreshTimerRef.current = null;
                    void refreshCockpit();
                }, 2000);
            }
            setEvents((prev) => [event, ...prev].slice(0, 120));
            if (event.event_type === "signal.generated") {
                setAlerts((prev) => [
                    {
                        id: `${event.event_id}-signal`,
                        level: "warning",
                        message: tRef.current("monitor.signalAlert", { mode: modeLabel(langRef.current, mode) }),
                        timestamp: event.timestamp,
                    },
                    ...prev,
                ].slice(0, 8));
            }
            if (event.event_type === "signal.rejected") {
                setAlerts((prev) => [
                    {
                        id: `${event.event_id}-rejected`,
                        level: "error",
                        message: tRef.current("monitor.signalRejectedAlert", {
                            reason: eventReasonCode(event),
                        }),
                        timestamp: event.timestamp,
                    },
                    ...prev,
                ].slice(0, 8));
            }
        });
        const unsubscribeError = apiClient.realtime.onError((event) => {
            if (event.mode !== mode)
                return;
            if (seenEventIdsRef.current.has(event.event_id))
                return;
            seenEventIdsRef.current.add(event.event_id);
            setWsErrorAt(Date.now());
            setAlerts((prev) => [
                {
                    id: `${event.event_id}-error`,
                    level: "error",
                    message: tRef.current("monitor.wsErrorAlert"),
                    timestamp: event.timestamp,
                },
                ...prev,
            ].slice(0, 8));
        });
        return () => {
            unsubscribeEvent();
            unsubscribeError();
            apiClient.realtime.unsubscribe(mode, ["market", "signal"]);
        };
    }, [mode, marketWatchlistKey]);
    const apiHealth = useMemo(() => {
        if (healthFetchError || error)
            return "degraded";
        if (healthSnapshot?.status === "ok")
            return "up";
        return "degraded";
    }, [error, healthFetchError, healthSnapshot]);
    const wsHealth = useMemo(() => {
        if (wsErrorAt && Date.now() - wsErrorAt < 15000)
            return "down";
        if (!lastWsEventAt)
            return "degraded";
        if (Date.now() - lastWsEventAt > 10000)
            return "degraded";
        return "up";
    }, [lastWsEventAt, wsErrorAt]);
    const marketDataHealth = useMemo(() => {
        const nowTs = Date.now();
        const primaryFresh = lastMarketPrimaryAt !== null && nowTs - lastMarketPrimaryAt <= 12000;
        const fallbackFresh = lastMarketFallbackAt !== null && nowTs - lastMarketFallbackAt <= 25000;
        if (primaryFresh)
            return "up";
        if (fallbackFresh)
            return "degraded";
        return "down";
    }, [lastMarketFallbackAt, lastMarketPrimaryAt]);
    const healthTone = (value) => {
        if (value === "up")
            return "text-emerald-600 dark:text-emerald-400";
        if (value === "degraded")
            return "text-amber-600 dark:text-amber-400";
        return "text-rose-600 dark:text-rose-400";
    };
    const buildHealthTimeline = (current) => {
        const now = Date.now();
        return Array.from({ length: 12 }).map((_, index) => {
            const ts = now - (11 - index) * 5000;
            let status = current;
            if (index < 8)
                status = "up";
            else if (index < 11)
                status = current === "down" ? "degraded" : "up";
            return { status, ts };
        });
    };
    const apiTimeline = useMemo(() => buildHealthTimeline(apiHealth), [apiHealth]);
    const wsTimeline = useMemo(() => buildHealthTimeline(wsHealth), [wsHealth]);
    const healthBg = (value) => {
        if (value === "up")
            return "bg-emerald-500";
        if (value === "degraded")
            return "bg-amber-500";
        return "bg-rose-500";
    };
    const formatHealthTime = (ts) => formatTime(ts, lang);
    const lastStatusChange = (timeline) => {
        if (timeline.length === 0)
            return null;
        const current = timeline[timeline.length - 1].status;
        for (let i = timeline.length - 1; i >= 0; i -= 1) {
            if (timeline[i].status !== current) {
                return timeline[i + 1];
            }
        }
        return timeline[0];
    };
    const apiLastChange = useMemo(() => lastStatusChange(apiTimeline), [apiTimeline]);
    const wsLastChange = useMemo(() => lastStatusChange(wsTimeline), [wsTimeline]);
    const visibleEvents = useMemo(() => (showEventsHistory ? events : events.slice(0, 12)), [events, showEventsHistory]);
    const availableEventTypes = useMemo(() => Array.from(new Set(events.map((event) => event.event_type))), [events]);
    const filteredEvents = useMemo(() => events.filter((event) => {
        if (eventTypeFilter !== "all" && event.event_type !== eventTypeFilter)
            return false;
        const query = eventSearch.trim().toLowerCase();
        if (!query)
            return true;
        return (event.event_type.toLowerCase().includes(query) ||
            JSON.stringify(event.payload).toLowerCase().includes(query));
    }), [eventSearch, eventTypeFilter, eventSearch, events]);
    const listEvents = useMemo(() => (showEventsHistory ? filteredEvents : filteredEvents.slice(0, 12)), [filteredEvents, showEventsHistory]);
    const chartFilteredEvents = useMemo(() => {
        if (chartWindow === "all")
            return filteredEvents;
        const now = Date.now();
        const windowMs = chartWindow === "30s" ? 30000 : chartWindow === "1m" ? 60000 : 5 * 60000;
        return filteredEvents.filter((event) => new Date(event.timestamp).getTime() >= now - windowMs);
    }, [chartWindow, filteredEvents]);
    const availableTickers = useMemo(() => Array.from(new Set(chartFilteredEvents
        .map((event) => eventTicker(event))
        .filter((ticker) => typeof ticker === "string" && ticker.length > 0))).sort(), [chartFilteredEvents]);
    useEffect(() => {
        if (chartScale !== "absolute")
            return;
        if (selectedTicker === "all" && availableTickers.length > 0) {
            setSelectedTicker(availableTickers[0]);
            return;
        }
        if (selectedTicker !== "all" && !availableTickers.includes(selectedTicker)) {
            setSelectedTicker(availableTickers[0] ?? "all");
        }
    }, [availableTickers, chartScale, selectedTicker]);
    useEffect(() => {
        if (!selectedEventId && listEvents.length > 0) {
            setSelectedEventId(listEvents[0].event_id);
            return;
        }
        if (selectedEventId && !events.some((event) => event.event_id === selectedEventId)) {
            setSelectedEventId(listEvents[0]?.event_id ?? null);
        }
    }, [events, listEvents, selectedEventId]);
    const selectedEvent = useMemo(() => events.find((event) => event.event_id === selectedEventId) ?? null, [events, selectedEventId]);
    const openEventDetails = useCallback((eventId) => {
        setSelectedEventId(eventId);
        setIsEventDetailsOpen(true);
    }, []);
    const closeEventDetails = useCallback(() => {
        setIsEventDetailsOpen(false);
    }, []);
    useEffect(() => {
        if (!isEventDetailsOpen)
            return;
        lastFocusedElementRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const container = eventDetailsRef.current;
        if (!container)
            return;
        const selectors = [
            "button:not([disabled])",
            "[href]",
            "input:not([disabled])",
            "select:not([disabled])",
            "textarea:not([disabled])",
            "[tabindex]:not([tabindex='-1'])",
        ].join(",");
        const focusables = Array.from(container.querySelectorAll(selectors));
        (focusables[0] ?? container).focus();
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                closeEventDetails();
                return;
            }
            if (event.key !== "Tab")
                return;
            const current = Array.from(container.querySelectorAll(selectors));
            if (current.length === 0) {
                event.preventDefault();
                container.focus();
                return;
            }
            const first = current[0];
            const last = current[current.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            }
            else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("keydown", onKeyDown);
            lastFocusedElementRef.current?.focus();
        };
    }, [closeEventDetails, isEventDetailsOpen]);
    const filteredAlerts = useMemo(() => alerts.filter((alert) => (alertFilter === "all" ? true : alert.level === alertFilter)), [alertFilter, alerts]);
    const timelineChart = useMemo(() => {
        if (chartFilteredEvents.length === 0)
            return null;
        const width = 760;
        const height = 220;
        const paddingX = 24;
        const paddingY = 18;
        const times = chartFilteredEvents.map((event) => new Date(event.timestamp).getTime());
        const min = Math.min(...times);
        const max = Math.max(...times);
        const span = Math.max(1, max - min);
        const x = (ts) => paddingX + ((ts - min) / span) * (width - paddingX * 2);
        const palette = ["#22c55e", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#14b8a6"];
        const market = chartFilteredEvents
            .filter((event) => event.event_type === "market.update")
            .map((event) => {
            const price = eventPrice(event) ?? Number.NaN;
            const ticker = eventTicker(event);
            return {
                id: event.event_id,
                ts: new Date(event.timestamp).getTime(),
                price,
                ticker,
                event,
            };
        })
            .filter((item) => Number.isFinite(item.price) && item.ticker)
            .sort((a, b) => a.ts - b.ts);
        if (market.length === 0)
            return null;
        const tickers = Array.from(new Set(market.map((item) => item.ticker))).sort();
        const tickersForScale = chartScale === "absolute" && selectedTicker !== "all" ? [selectedTicker] : tickers;
        const rawSeries = tickers.map((ticker, index) => {
            const points = market.filter((item) => item.ticker === ticker);
            const bucketMs = chartWindow === "30s" ? 5000 : chartWindow === "1m" ? 10000 : 30000;
            const bucket = new Map();
            points.forEach((point) => {
                const key = Math.floor(point.ts / bucketMs) * bucketMs;
                const list = bucket.get(key);
                if (!list)
                    bucket.set(key, [point]);
                else
                    list.push(point);
            });
            const aggregated = Array.from(bucket.entries())
                .sort((a, b) => a[0] - b[0])
                .map(([, list]) => {
                const avgPrice = list.reduce((sum, item) => sum + item.price, 0) / list.length;
                return {
                    ...list[list.length - 1],
                    price: avgPrice,
                };
            });
            return {
                ticker,
                color: palette[index % palette.length],
                points: aggregated,
                lastTs: aggregated[aggregated.length - 1]?.ts ?? 0,
                firstPrice: aggregated[0]?.price ?? null,
                lastPrice: aggregated[aggregated.length - 1]?.price ?? null,
            };
        });
        const scopedSeries = rawSeries.filter((series) => tickersForScale.includes(series.ticker));
        const seriesBasePrice = (price) => typeof price === "number" && Number.isFinite(price) && price > 0 ? price : 1;
        const allValues = scopedSeries.flatMap((series) => {
            if (chartScale === "normalized") {
                const base = seriesBasePrice(series.firstPrice);
                return series.points.map((point) => (point.price / base) * 100);
            }
            return series.points.map((point) => point.price);
        }).filter((value) => Number.isFinite(value));
        if (allValues.length === 0)
            return null;
        const minPrice = allValues.length > 0 ? Math.min(...allValues) : 0;
        const maxPrice = allValues.length > 0 ? Math.max(...allValues) : 1;
        const priceSpan = Math.max(0.0001, maxPrice - minPrice);
        const yPrice = (value) => {
            if (!Number.isFinite(value))
                return height - paddingY;
            const y = height - paddingY - ((value - minPrice) / priceSpan) * (height - paddingY * 2);
            return Number.isFinite(y) ? y : height - paddingY;
        };
        const marketSeries = scopedSeries
            .filter((series) => series.points.length > 0)
            .map((series) => {
            const base = seriesBasePrice(series.firstPrice);
            const plotted = series.points.map((point) => ({
                ...point,
                value: chartScale === "normalized" ? (point.price / base) * 100 : point.price,
            }));
            const path = plotted
                .filter((point) => Number.isFinite(point.value))
                .map((point, idx) => `${idx === 0 ? "M" : "L"} ${x(point.ts).toFixed(2)} ${yPrice(point.value).toFixed(2)}`)
                .join(" ");
            return {
                ...series,
                path,
                points: plotted
                    .filter((point) => Number.isFinite(point.value))
                    .map((point) => ({
                    id: point.id,
                    cx: x(point.ts),
                    cy: yPrice(point.value),
                    price: point.price,
                    value: point.value,
                    event: point.event,
                })),
            };
        })
            .filter((series) => series.points.length > 0);
        if (marketSeries.length === 0)
            return null;
        const latestSeries = [...scopedSeries].sort((a, b) => b.lastTs - a.lastTs)[0];
        const latestMarket = latestSeries
            ? {
                ticker: latestSeries.ticker,
                price: latestSeries.lastPrice,
            }
            : null;
        const marketDeltaPct = latestSeries && latestSeries.firstPrice && latestSeries.lastPrice
            ? ((latestSeries.lastPrice - latestSeries.firstPrice) / latestSeries.firstPrice) * 100
            : null;
        const signalMarkers = chartFilteredEvents
            .filter((event) => event.event_type === "signal.generated")
            .filter((event) => {
            if (chartScale !== "absolute" || selectedTicker === "all")
                return true;
            return eventTicker(event) === selectedTicker;
        })
            .map((event) => ({
            id: event.event_id,
            ts: new Date(event.timestamp).getTime(),
            y: paddingY + 8,
            event,
        }));
        const errorMarkers = chartFilteredEvents
            .filter((event) => event.event_type.includes("error"))
            .map((event) => ({
            id: event.event_id,
            ts: new Date(event.timestamp).getTime(),
            y: height - paddingY - 8,
            event,
        }));
        return {
            width,
            height,
            minPrice,
            maxPrice,
            marketSeries,
            signalMarkers: signalMarkers.map((item) => ({
                id: item.id,
                cx: x(item.ts),
                cy: item.y,
                event: item.event,
            })),
            errorMarkers: errorMarkers.map((item) => ({
                id: item.id,
                cx: x(item.ts),
                cy: item.y,
                event: item.event,
            })),
            signalsCount: signalMarkers.length,
            errorsCount: errorMarkers.length,
            lastPrice: latestMarket?.price ?? null,
            lastPriceTicker: latestMarket?.ticker ?? null,
            marketDeltaPct,
            min,
            max,
            yTicks: [
                { label: maxPrice, y: yPrice(maxPrice) },
                { label: minPrice + priceSpan / 2, y: yPrice(minPrice + priceSpan / 2) },
                { label: minPrice, y: yPrice(minPrice) },
            ],
        };
    }, [chartFilteredEvents, chartScale, chartWindow, selectedTicker]);
    const eventBrief = (event) => {
        if (event.event_type === "market.update") {
            const ticker = eventTicker(event);
            const price = eventPrice(event);
            return `${ticker} ${typeof price === "number" ? formatPrice(price) : ""}`.trim();
        }
        if (event.event_type === "signal.generated") {
            const signal = eventSignal(event);
            const ticker = eventTicker(event);
            return `${signal} ${ticker}`.trim();
        }
        if (event.event_type === "signal.rejected") {
            return `${eventPipelineStage(event)} · ${eventReasonCode(event)}`;
        }
        return t("monitor.noDetails");
    };
    const ackAlert = (id) => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    };
    const clearAlerts = (scope) => {
        if (scope === "all") {
            setAlerts([]);
            return;
        }
        setAlerts((prev) => prev.filter((alert) => !(alertFilter === "all" ? true : alert.level === alertFilter)));
    };
    return (_jsxs(PageLayout, { title: t("dashboard.title"), children: [_jsx(MobileSectionsMenu, {}), _jsxs("div", { className: WORKSPACE_SHELL_GRID, children: [_jsxs("aside", { className: WORKSPACE_ASIDE, children: [_jsx("h2", { id: "workspace-nav-heading", className: "mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground", children: t("nav.section") }), _jsx(WorkspaceNav, {})] }), _jsxs("section", { "aria-labelledby": "dashboard-content-heading", children: [_jsx("h2", { id: "dashboard-content-heading", className: "sr-only", children: t("dashboard.title") }), _jsxs("p", { className: "mb-6 text-sm text-muted-foreground", children: [t("common.user"), ": ", _jsx("span", { className: "font-medium text-foreground", children: session?.user.email })] }), _jsx(ModeScopeBanner, { mode: mode }), loading ? _jsx(LoadingMessage, { text: t("dashboard.loadSummary") }) : null, error ? (_jsx(ErrorWithRetry, { message: error, onRetry: () => void load(), disabled: loading, className: "mb-4" })) : null, !loading && !error && !summary ? _jsx(EmptyState, { text: t("dashboard.emptySummary") }) : null, !loading && !error && summary ? (_jsxs(Card, { className: cn("mb-8", modeAccentBorderClass(mode)), children: [_jsx(CardTitle, { className: "mb-4", children: t("dashboard.summaryTitle", { mode: modeLabel(lang, summary.mode) }) }), _jsxs("ul", { className: "grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4", children: [_jsxs("li", { className: "rounded-lg bg-muted/40 px-3 py-2 dark:bg-muted/20", children: [_jsxs("span", { className: "text-muted-foreground", children: [t("dashboard.portfolio"), ": "] }), _jsxs("span", { className: "font-medium text-foreground", children: [summary.portfolio_value.value, " ", summary.portfolio_value.currency] })] }), _jsxs("li", { className: "rounded-lg bg-muted/40 px-3 py-2 dark:bg-muted/20", children: [_jsxs("span", { className: "text-muted-foreground", children: [t("dashboard.pnlDaily"), ": "] }), _jsxs("span", { className: "font-medium text-foreground", children: [summary.pnl_daily.value, " ", summary.pnl_daily.currency] })] }), _jsxs("li", { className: "rounded-lg bg-muted/40 px-3 py-2 dark:bg-muted/20", children: [_jsxs("span", { className: "text-muted-foreground", children: [t("dashboard.activeStrategies"), ": "] }), _jsx("span", { className: "font-medium text-foreground", children: summary.active_strategies })] }), _jsxs("li", { className: "rounded-lg bg-muted/40 px-3 py-2 dark:bg-muted/20", children: [_jsxs("span", { className: "text-muted-foreground", children: [t("dashboard.openOrders"), ": "] }), _jsx("span", { className: "font-medium text-foreground", children: summary.open_orders })] })] })] })) : null, _jsx(BrokerPortfolioComparisonCard, { mode: mode }), !loading && !error && cockpit ? (_jsxs(_Fragment, { children: [_jsx(TradingPnlSummary, { cockpit: cockpit, mode: mode, className: "mb-6" }), _jsxs(Card, { className: cn("mb-6", modeAccentBorderClass(mode)), children: [_jsxs("div", { className: "mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(CardTitle, { className: "mb-0", children: t("pro.riskCockpitTitle") }), _jsx(ModeBadge, { mode: mode })] }), _jsx(Link, { to: "/journal", className: "text-sm font-medium text-purple-600 underline-offset-2 hover:underline dark:text-purple-400", children: t("pro.openExecutionJournal") })] }), _jsx("p", { className: "mb-4 text-xs text-muted-foreground", children: t("pro.riskCockpitHint") }), _jsxs("div", { className: "mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3", children: [_jsx(LimitUsageBar, { label: t("limits.dailyNotional"), used: cockpit.notional_used_today, limit: cockpit.policy_ref.max_notional_per_day, unit: cockpit.currency }), _jsx(LimitUsageBar, { label: t("limits.dailyTrades"), used: cockpit.trades_used_today, limit: cockpit.policy_ref.max_trades_per_day, format: "integer" }), _jsx(LimitUsageBar, { label: t("limits.dailyLoss"), used: cockpit.daily_loss_used, limit: cockpit.policy_ref.max_daily_loss, unit: cockpit.currency })] }), cockpit.exposure_by_ticker.length > 0 ? (_jsx("div", { className: TABLE_SCROLL, children: _jsxs("table", { className: "w-full min-w-[280px] text-left text-sm", children: [_jsx("thead", { children: _jsxs("tr", { className: "text-muted-foreground", children: [_jsx("th", { className: "pb-1 pr-2 font-medium", children: t("orders.colTicker") }), _jsx("th", { className: "pb-1 pr-2 font-medium", children: t("pro.exposureNotional") }), _jsx("th", { className: "pb-1 font-medium", children: t("pro.pctOfMax") })] }) }), _jsx("tbody", { children: cockpit.exposure_by_ticker.map((row) => (_jsxs("tr", { className: "border-t border-border/60", children: [_jsx("td", { className: "py-1.5 pr-2 font-mono text-foreground", children: row.ticker }), _jsxs("td", { className: "py-1.5 pr-2", children: [row.notional.toFixed(0), " ", cockpit.currency] }), _jsxs("td", { className: "py-1.5", children: [row.pct_of_max_exposure.toFixed(1), "%"] })] }, row.ticker))) })] }) })) : (_jsx("p", { className: "text-sm text-muted-foreground", children: t("pro.noExposureRows") }))] })] })) : null, _jsxs(Card, { className: "mb-6", children: [_jsx(CardTitle, { className: "mb-4", children: t("monitor.healthTitle") }), _jsxs("ul", { className: "space-y-3 text-sm", children: [_jsxs("li", { className: "rounded-lg bg-muted/30 px-3 py-2", children: [_jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-muted-foreground", children: t("monitor.apiHealth") }), _jsx("span", { className: `font-medium ${healthTone(apiHealth)}`, children: t(`monitor.status.${apiHealth}`) })] }), _jsx("div", { className: "flex gap-1", children: apiTimeline.map((item, index) => (_jsx("span", { className: `h-2 flex-1 rounded-full ${healthBg(item.status)}`, title: `${formatHealthTime(item.ts)} · ${t(`monitor.status.${item.status}`)}` }, `api-${index}`))) }), _jsxs("p", { className: "mt-1 text-xs text-muted-foreground", children: [t("monitor.lastChange"), ": ", apiLastChange ? formatHealthTime(apiLastChange.ts) : "—"] }), healthSnapshot ? (_jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: t("monitor.apiHealthDetail", {
                                                            ws: healthSnapshot.ws_connections,
                                                            uptime: Math.round(healthSnapshot.uptime_sec),
                                                        }) })) : null] }), _jsxs("li", { className: "rounded-lg bg-muted/30 px-3 py-2", children: [_jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-muted-foreground", children: t("monitor.wsHealth") }), _jsx("span", { className: `font-medium ${healthTone(wsHealth)}`, children: t(`monitor.status.${wsHealth}`) })] }), _jsx("div", { className: "flex gap-1", children: wsTimeline.map((item, index) => (_jsx("span", { className: `h-2 flex-1 rounded-full ${healthBg(item.status)}`, title: `${formatHealthTime(item.ts)} · ${t(`monitor.status.${item.status}`)}` }, `ws-${index}`))) }), _jsxs("p", { className: "mt-1 text-xs text-muted-foreground", children: [t("monitor.lastChange"), ": ", wsLastChange ? formatHealthTime(wsLastChange.ts) : "—"] })] }), _jsxs("li", { className: "rounded-lg bg-muted/30 px-3 py-2", children: [_jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [_jsx("span", { className: "text-muted-foreground", children: t("monitor.marketDataHealth") }), _jsx("span", { className: `font-medium ${healthTone(marketDataHealth)}`, children: t(`monitor.status.${marketDataHealth}`) })] }), _jsxs("p", { className: "text-xs text-muted-foreground", children: [t("monitor.marketDataSource"), ": ", lastMarketSource ? t(`monitor.marketDataSourceValue.${lastMarketSource}`) : "—"] }), _jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: t("monitor.marketDataHint") })] })] }), _jsxs("div", { className: "mt-3 flex items-center justify-between text-xs text-muted-foreground", children: [_jsx("span", { children: formatHealthTime(apiTimeline[0]?.ts ?? Date.now()) }), _jsx("span", { children: t("monitor.now") })] }), _jsx("p", { className: "mt-3 text-xs text-muted-foreground", children: t("monitor.healthTimelineHint") })] }), _jsx("div", { className: "grid gap-6", children: _jsxs(Card, { children: [_jsxs("div", { className: "mb-4 flex items-center justify-between gap-2", children: [_jsxs("div", { children: [_jsx(CardTitle, { children: t("monitor.timelineTitle") }), marketWatchlist.length > 0 ? (_jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: t("monitor.watchlistHint", { tickers: marketWatchlist.join(", ") }) })) : null] }), _jsx("button", { type: "button", className: "text-xs font-medium text-muted-foreground underline-offset-4 hover:underline", onClick: () => setShowEventsHistory((prev) => !prev), children: showEventsHistory ? t("monitor.hideHistory") : t("monitor.showHistory") })] }), events.length === 0 ? (_jsx(EmptyState, { text: t("monitor.timelineEmpty") })) : (_jsxs("div", { className: "space-y-3", children: [_jsxs("div", { className: "grid gap-2 sm:grid-cols-3", children: [_jsxs("select", { value: eventTypeFilter, onChange: (e) => setEventTypeFilter(e.target.value), className: "h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground", children: [_jsx("option", { value: "all", children: t("monitor.filterAllTypes") }), availableEventTypes.map((type) => (_jsx("option", { value: type, children: type }, type)))] }), _jsx("input", { value: eventSearch, onChange: (e) => setEventSearch(e.target.value), placeholder: t("monitor.filterSearch"), className: "h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground sm:col-span-2" })] }), _jsxs("div", { className: "inline-flex rounded-full border border-border bg-background p-1", children: [_jsx("button", { type: "button", onClick: () => setChartScale("normalized"), className: `rounded-full px-3 py-1 text-xs font-medium ${chartScale === "normalized" ? "bg-purple-600 text-white" : "text-muted-foreground hover:bg-muted"}`, children: t("monitor.scaleNormalized") }), _jsx("button", { type: "button", onClick: () => setChartScale("absolute"), className: `rounded-full px-3 py-1 text-xs font-medium ${chartScale === "absolute" ? "bg-purple-600 text-white" : "text-muted-foreground hover:bg-muted"}`, children: t("monitor.scaleAbsolute") })] }), _jsx("div", { className: "inline-flex rounded-full border border-border bg-background p-1", children: ["30s", "1m", "5m", "all"].map((window) => (_jsx("button", { type: "button", onClick: () => setChartWindow(window), className: `rounded-full px-3 py-1 text-xs font-medium ${chartWindow === window ? "bg-purple-600 text-white" : "text-muted-foreground hover:bg-muted"}`, children: t(`monitor.window.${window}`) }, window))) }), chartScale === "absolute" ? (_jsxs("select", { value: selectedTicker, onChange: (e) => setSelectedTicker(e.target.value), className: "h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground", children: [availableTickers.length === 0 ? _jsx("option", { value: "all", children: "\u2014" }) : null, availableTickers.map((ticker) => (_jsx("option", { value: ticker, children: ticker }, ticker)))] })) : null, timelineChart ? (_jsxs("div", { className: "rounded-lg border border-border bg-muted/10 p-3", children: [_jsxs("div", { className: "mb-2 grid gap-2 text-xs sm:grid-cols-3", children: [_jsxs("div", { className: "rounded bg-background px-2 py-1 text-muted-foreground", children: [t("monitor.lastPrice"), ":", " ", _jsx("span", { className: "font-medium text-foreground", children: timelineChart.lastPrice !== null
                                                                                ? `${timelineChart.lastPriceTicker ?? ""} ${formatPrice(timelineChart.lastPrice)}`
                                                                                : "—" })] }), _jsxs("div", { className: "rounded bg-background px-2 py-1 text-muted-foreground", children: [t("monitor.priceDelta"), ":", " ", _jsx("span", { className: timelineChart.marketDeltaPct === null
                                                                                ? "text-foreground"
                                                                                : timelineChart.marketDeltaPct >= 0
                                                                                    ? "text-emerald-500"
                                                                                    : "text-rose-500", children: timelineChart.marketDeltaPct === null ? "—" : `${timelineChart.marketDeltaPct.toFixed(2)}%` })] }), _jsxs("div", { className: "rounded bg-background px-2 py-1 text-muted-foreground", children: [t("monitor.signalsErrors"), ":", " ", _jsxs("span", { className: "font-medium text-foreground", children: [timelineChart.signalsCount, "/", timelineChart.errorsCount] })] })] }), _jsxs("svg", { viewBox: `0 0 ${timelineChart.width} ${timelineChart.height}`, className: "h-48 w-full", children: [timelineChart.yTicks.map((tick, idx) => (_jsxs("g", { children: [_jsx("line", { x1: "18", x2: timelineChart.width - 18, y1: tick.y, y2: tick.y, stroke: "rgba(148,163,184,0.2)", strokeDasharray: "4 3" }), _jsx("text", { x: "2", y: tick.y - 2, fontSize: "10", fill: "currentColor", className: "text-muted-foreground", children: chartScale === "normalized" ? `${tick.label.toFixed(1)}` : formatPrice(tick.label) })] }, `tick-${idx}`))), _jsx("line", { x1: "18", x2: timelineChart.width - 18, y1: timelineChart.height - 18, y2: timelineChart.height - 18, stroke: "rgba(148,163,184,0.35)" }), timelineChart.marketSeries.map((series) => (_jsxs("g", { children: [series.path ? _jsx("path", { d: series.path, fill: "none", stroke: series.color, strokeWidth: "2.5" }) : null, series.points.map((point) => (_jsx("circle", { cx: point.cx, cy: point.cy, r: selectedEventId === point.id ? 4.5 : 3.2, fill: series.color, stroke: selectedEventId === point.id ? "#ffffff" : "none", strokeWidth: selectedEventId === point.id ? 1.4 : 0, onClick: () => openEventDetails(point.event.event_id), children: _jsx("title", { children: `${series.ticker} · ${chartScale === "normalized" ? point.value.toFixed(2) : formatPrice(point.price)}` }) }, point.id)))] }, series.ticker))), timelineChart.signalMarkers.map((point) => (_jsx("polygon", { points: `${point.cx},${point.cy - 5} ${point.cx - 5},${point.cy + 5} ${point.cx + 5},${point.cy + 5}`, className: selectedEventId === point.event.event_id ? "fill-purple-300" : "fill-purple-500", onClick: () => openEventDetails(point.event.event_id), children: _jsx("title", { children: point.event.event_type }) }, point.id))), timelineChart.errorMarkers.map((point) => (_jsx("rect", { x: point.cx - 4, y: point.cy - 4, width: 8, height: 8, transform: `rotate(45 ${point.cx} ${point.cy})`, className: selectedEventId === point.event.event_id ? "fill-rose-300" : "fill-rose-500", onClick: () => openEventDetails(point.event.event_id), children: _jsx("title", { children: point.event.event_type }) }, point.id)))] }), _jsxs("div", { className: "mt-1 flex items-center gap-4 text-xs text-muted-foreground", children: [timelineChart.marketSeries.map((series) => (_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx("span", { className: "h-2 w-2 rounded-full", style: { backgroundColor: series.color } }), series.ticker] }, `legend-${series.ticker}`))), _jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx("span", { className: "h-0 w-0 border-x-[5px] border-b-[8px] border-x-transparent border-b-purple-500" }), "signal.generated"] }), _jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx("span", { className: "h-2 w-2 rotate-45 bg-rose-500" }), "error"] })] }), _jsx("p", { className: "mt-1 text-xs text-muted-foreground", children: chartScale === "absolute"
                                                                ? t("monitor.absoluteHint", { ticker: selectedTicker })
                                                                : t("monitor.normalizedHint") }), _jsxs("div", { className: "mt-1 flex items-center justify-between text-xs text-muted-foreground", children: [_jsx("span", { children: formatTime(timelineChart.min, lang) }), _jsx("span", { children: formatTime(timelineChart.max, lang) })] })] })) : (_jsx(EmptyState, { text: t("monitor.filteredEmpty") })), _jsx("div", { className: "grid gap-3", children: _jsx("ul", { className: "max-h-[26rem] space-y-2 overflow-auto pr-1", children: listEvents.map((event) => (_jsx("li", { children: _jsx("button", { type: "button", onClick: () => openEventDetails(event.event_id), className: `w-full rounded-lg border p-3 text-left text-sm transition ${selectedEventId === event.event_id
                                                                    ? "border-purple-500 bg-purple-500/10"
                                                                    : "border-border hover:bg-muted/30"}`, children: _jsxs("div", { className: "flex items-start gap-2", children: [_jsx(CircleDot, { className: "mt-0.5 h-4 w-4 shrink-0 text-purple-500", "aria-hidden": true }), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "truncate font-medium text-foreground", children: event.event_type }), _jsx("p", { className: "truncate text-xs text-muted-foreground", children: eventBrief(event) }), _jsxs("p", { className: "text-xs text-muted-foreground", children: [formatTime(event.timestamp, lang), " \u00B7", " ", modeLabel(lang, event.mode)] })] })] }) }) }, event.event_id))) }) })] }))] }) }), _jsx(AlertsCard, { t: t, lang: lang, alerts: alerts, filteredAlerts: filteredAlerts, alertFilter: alertFilter, onFilterChange: setAlertFilter, onClearFiltered: () => clearAlerts("filtered"), onClearAll: () => clearAlerts("all"), onAck: ackAlert })] })] }), isEventDetailsOpen && selectedEvent ? (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4", onClick: closeEventDetails, children: _jsxs("div", { ref: eventDetailsRef, tabIndex: -1, className: "max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl border border-border bg-card p-4 shadow-xl", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "mb-3 flex items-center justify-between gap-3", children: [_jsx("p", { className: "text-sm font-medium text-foreground", children: t("monitor.eventDetailsTitle") }), _jsx(Button, { type: "button", variant: "secondary", size: "sm", onClick: closeEventDetails, children: t("common.cancel") })] }), _jsxs("div", { className: "space-y-3 text-sm", children: [_jsxs("div", { className: "space-y-1", children: [_jsxs("p", { className: "break-words text-muted-foreground", children: [t("monitor.eventType"), ": ", _jsx("span", { className: "text-foreground", children: selectedEvent.event_type })] }), _jsxs("p", { className: "text-muted-foreground", children: [t("monitor.eventMode"), ": ", _jsx("span", { className: "text-foreground", children: modeLabel(lang, selectedEvent.mode) })] }), _jsxs("p", { className: "text-muted-foreground", children: [t("monitor.eventTime"), ":", " ", _jsx("span", { className: "text-foreground", children: formatDateTime(selectedEvent.timestamp, lang) })] }), _jsxs("p", { className: "break-all text-muted-foreground", children: [t("monitor.eventId"), ": ", _jsx("span", { className: "font-mono text-foreground", children: selectedEvent.event_id })] }), _jsxs("p", { className: "text-muted-foreground", children: [t("monitor.pipelineStage"), ": ", _jsx("span", { className: "text-foreground", children: eventPipelineStage(selectedEvent) })] }), _jsxs("p", { className: "text-muted-foreground", children: [t("monitor.reasonCode"), ": ", _jsx("span", { className: "font-mono text-foreground", children: eventReasonCode(selectedEvent) })] })] }), _jsxs("div", { children: [_jsxs("p", { className: "mb-1 text-muted-foreground", children: [t("monitor.eventPayload"), ":"] }), _jsx("pre", { className: "max-h-64 overflow-auto rounded-md border border-border bg-background p-2 text-xs text-foreground", children: JSON.stringify(selectedEvent.payload, null, 2) })] })] })] }) })) : null] }));
};
export default Dashboard;
